import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { cn } from "@/libs/utils";
import { useTrans } from "src/utils/useTrans";
import { Accordion } from "src/common/accordion";
import { UxUtils } from "src/utils/UxUtils";
import { SupportMainContentsTree } from "@/v2-components/pages/support/components/shared/support-main-contents-tree/support-main-contents-tree";
import Container from "@/components/commons/Container";
import SupportBannerImgSection from "@/v2-components/pages/support/shared/support-banner-img-section";
import { SupportFaqSlideSection } from "@/v2-components/pages/support/components/faq/support-faq-slide-section";
import type { SupportCategory } from "@/v2-components/pages/support/types/support-data";
import { findCategoryById } from "@/v2-components/pages/support/utils/help-center-utils";

interface FaqPost {
  id: string;
  category: string;
  title: string;
  content: string;
}

interface FaqData {
  faqTree: SupportCategory;
  firstCategorySlug: string;
  posts: FaqPost[];
}

const Text = ({ ...rest }: React.HTMLAttributes<HTMLHeadElement>) => (
  <h3
    className={cn(["text-[22px] font-400 leading-[27.5px] smd:text-4xl"])}
    {...rest}
  />
);

const DESKTOP_BANNER_IMG = "/images/support/faq/faq-banner.jpg";
const MOBILE_BANNER_IMAGE = "/images/support/faq/faq-mobile-banner.jpg";

export default function FaqsPage({ faqData }: { faqData: FaqData }) {
  const [openIds, setOpenIds] = useState<string[]>([]);
  const { query }: any = useRouter();
  const { t } = useTrans(["support"]);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const { faqTree, firstCategorySlug, posts } = faqData;
  const currentSlug = query?.categories || firstCategorySlug;

  const filteredPosts = useMemo(
    () => posts.filter((p) => p.category === currentSlug),
    [posts, currentSlug],
  );

  const currentLabel =
    findCategoryById(faqTree.children ?? [], currentSlug)?.label ?? "";

  useEffect(() => {
    const { ID } = query;
    if (ID) {
      setOpenIds((prev) => (prev.includes(ID) ? prev : [...prev, ID]));
    }
  }, [query]);

  useEffect(() => {
    if (query.ID) {
      UxUtils.moveTo(`${query.ID}`);
    }
  }, [query.ID]);

  const translated = useMemo(() => {
    const translate = (node: SupportCategory): SupportCategory => {
      const { children = [] } = node;
      return {
        ...node,
        label: t(node.label),
        children: children.map(translate),
      };
    };
    return translate(faqTree);
  }, [faqTree, t]);

  return (
    <Fragment>
      <div className="w-full bg-white">
        <SupportFaqSlideSection faqs={translated} />
        <SupportBannerImgSection
          bannerImg={DESKTOP_BANNER_IMG}
          mobileBannerImg={MOBILE_BANNER_IMAGE}
          bannerTitle={"FAQ"}
          isTitleShadow={true}
        />

        <Container>
          <div className="mx-auto flex flex-col gap-x-5 pb-12 pt-[34px] smd:flex-row smd:pb-16 smd:pt-[60px] md:gap-x-10 lg:gap-x-[88px]">
            <div
              className={cn(
                "hidden flex-col gap-y-[16px] smd:flex smd:min-w-[320px] smd:max-w-[320px] smd:gap-y-8 lg:w-full",
              )}
            >
              <Text>{t("Frequently Asked Questions")}</Text>
              <SupportMainContentsTree
                query="categories"
                className="pt-0"
                tree={translated.children || []}
              />
            </div>

            <div className={cn("flex grow flex-col gap-y-5")}>
              <h2 className="text-3xl font-400 smd:text-4xl">
                {t(currentLabel)}{" "}
              </h2>
              <div>
                {mounted &&
                  filteredPosts.map((item) => {
                    const opened = openIds.includes(item.id);
                    return (
                      <div key={item.id} id={item.id} className="relative">
                        <Accordion
                          title={item.title}
                          content={item.content}
                          opened={opened}
                          onToggleOpen={() => {
                            setOpenIds((prev) =>
                              prev.includes(item.id)
                                ? prev.filter((id) => id !== item.id)
                                : [...prev, item.id],
                            );
                          }}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </Fragment>
  );
}
